/**
 * Erişim ve şeffaflık motoru (6. motor).
 *
 * Data/code availability, COI, funding ve ethics beyanlarını; DOI ve URL
 * biçimlerini **yalnızca regex** ile kontrol eder. Hiçbir ağ isteği yapılmaz.
 */

import type { Issue, AccessResult, AccessCheck, AccessCheckKey, ExtractedDocument } from '../../types';
import type { DocumentIndex } from '../../text/document';
import { excerpt } from '../../text/normalize';

export interface AccessEngineOutput {
	result: AccessResult;
	issues: Issue[];
}

const DATA_RE =
	/(?:data\s+availability|data\s+(?:are|is|that)[^.\n]{0,100}\bavailable|data\s+available|data[^.\n]{0,80}(?:repository|deposited|archived|upon\s+request)|supplementary\s+(?:material|data)|veri\s+erişimi|veriler[^.\n]{0,80}(?:mevcut|erişilebilir|temin|paylaş)|talep\s+üzerine|ek\s+materyal)/iu;
const COI_RE =
	/(?:conflicts?\s+of\s+interest|competing\s+interests?|declarations?\s+of\s+interest|çıkar\s+çatışması|çıkar\s+ilişkisi)/iu;
const FUNDING_RE =
	/(?:funded\s+by|funding|grant|supported\s+by|destekleyen|finans|fon\b|hibe|teşekkür|acknowledg(?:e)?ment)/iu;
const ETHICS_RE =
	/(?:ethics\s+committee|ethical\s+approval|institutional\s+review\s+board|\bIRB\b|informed\s+consent|etik\s+kurul|etik\s+komite|onam)/iu;
const HUMAN_RE =
	/(?:informed\s+consent|ethics\s+committee|institutional\s+review\s+board|\bIRB\b|human\s+subjects|participants?|patients?|volunteers?|respondents?|katılımcı|hasta|gönüllü|onam|bilgilendirilmiş\s+onam|onam\s+formu|araştırmaya\s+katıl|ölçek\s+uyarlama|etik\s+kurul|etik\s+onay|denek|örneklem|anket|görüşme|öğretmen|öğrenci)/iu;
const STRONG_HUMAN_RE =
	/(?:bilgilendirilmiş\s+onam|onam\s+formu|informed\s+consent|katılımcı|araştırmaya\s+katıl|ölçek\s+uyarlama|gönüllü|etik\s+onay)/iu;
const LAB_RE =
	/(?:electrode|electrolysis|electrochemical|spectroscop|chromatograph|catalyst|electrocatal|voltammetr|in\s+vitro|cell\s+culture|corrosion|titration|electrolyte|elektrot|elektroliz|spektroskopi|katalizör|korozyon|titrasyon|voltametri|elektrolit)/iu;
const ANIMAL_RE = /(?:\bmice\b|\brats?\b|\bmurine\b|\banimals?\b|hayvan|fare|sıçan)/iu;
const ETHICS_NA_RE =
	/(?:(?:institutional\s+review\s+board|\bIRB\b|informed\s+consent|ethics\s+committee|etik\s+kurul|onam)[^.\n]{0,90}(?:not\s+applicable|n\/a|uygulanamaz|gerekli\s+değil)|(?:not\s+applicable|n\/a|uygulanamaz)[^.\n]{0,60}(?:IRB|consent|ethics|etik|onam))/iu;
const COMPUTATIONAL_RE =
	/(?:\b(?:algorithm|software|source\s+code|simulation|machine\s+learning|deep\s+learning|python|matlab|stata)\b|github\.com|gitlab\.com|bitbucket\.org|yazılım|kaynak\s+kod|algoritma|simülasyon)/iu;
const CODE_RE =
	/(?:github\.com|gitlab\.com|bitbucket\.org|source\s+code|code\s+(?:is\s+)?available|kod\s+erişimi|kaynak\s+kod)/iu;
const EMAIL_IN_TEXT_RE = /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g;
const ORCID_RE = /\b\d{4}-\d{4}-\d{4}-\d{3}[\dX]\b/g;
const GENERIC_AUTHOR_RE =
	/^(?:unknown|user|administrator|admin|anonymous|microsoft|adobe|latex|pdftex|google|libreoffice|writer|word|overleaf)$/i;

const DOI_VALID_RE = /\b10\.\d{4,9}\/[-._;()/:a-z0-9]+\b/gi;
const DOI_SHORT_RE = /\b10\.\d{1,3}\/[^\s,;]+/gi;
const DOI_PREFIX_RE = /\bdoi:\s*([^\s,;]+)/gi;
const DOI_URL_RE = /https?:\/\/(?:dx\.)?doi\.org\/([^\s,;]+)/gi;
const URL_RE = /https?:\/\/[^\s<>"')\]]+/gi;
const URL_EMPTY_RE = /https?:\/\/(?=\s|$)/gi;

function trimUrl(url: string): string {
	return url.replace(/[.,;:]+$/, '');
}

function firstMatch(text: string, re: RegExp): { index: number; text: string } | undefined {
	re.lastIndex = 0;
	const m = re.exec(text);
	return m ? { index: m.index, text: m[0] } : undefined;
}

function check(
	key: AccessCheckKey,
	status: AccessCheck['status'],
	evidence?: string,
	count?: number
): AccessCheck {
	return { key, status, evidence, count };
}

export function runAccessEngine(
	index: DocumentIndex,
	meta?: ExtractedDocument['meta']
): AccessEngineOutput {
	const text = index.text;
	const issues: Issue[] = [];
	const push = (issue: Omit<Issue, 'id' | 'decisive'> & { decisive?: boolean }) => {
		issues.push({
			...issue,
			id: `access-${issues.length + 1}`,
			decisive: issue.decisive ?? issue.confidence !== 'low'
		});
	};

	const data = firstMatch(text, DATA_RE);
	const coi = firstMatch(text, COI_RE);
	const funding = firstMatch(text, FUNDING_RE);
	const ethics = firstMatch(text, ETHICS_RE);
	const ethicsNa = ETHICS_NA_RE.test(text);
	const humanException =
		/informed\s+consent|onam|etik\s+kurul|IRB|institutional\s+review\s+board|human\s+subjects|patients?|hasta/iu.test(
			text
		);
	const human =
		!ethicsNa &&
		(STRONG_HUMAN_RE.test(text) ||
			(!ANIMAL_RE.test(text) && HUMAN_RE.test(text) && !(LAB_RE.test(text) && !humanException)));
	const computational = COMPUTATIONAL_RE.test(text);
	const code = firstMatch(text, CODE_RE);

	/* DOI formatı ------------------------------------------------------- */
	const validDois = new Set<string>();
	for (const m of text.matchAll(DOI_VALID_RE)) validDois.add(m[0].toLowerCase());

	const invalidDoiCandidates = new Set<string>();
	for (const m of text.matchAll(DOI_SHORT_RE)) {
		const candidate = m[0].replace(/[.,;:]+$/, '');
		DOI_VALID_RE.lastIndex = 0;
		if (!DOI_VALID_RE.test(candidate)) invalidDoiCandidates.add(candidate);
	}
	for (const re of [DOI_PREFIX_RE, DOI_URL_RE]) {
		re.lastIndex = 0;
		for (const m of text.matchAll(re)) {
			const candidate = m[1].replace(/[.,;:]+$/, '');
			if (!/^10\.\d{4,9}\/[-._;()/:a-z0-9]+$/i.test(candidate)) {
				invalidDoiCandidates.add(candidate);
			}
		}
	}
	const invalidDois = [...invalidDoiCandidates];

	/* URL formatı ------------------------------------------------------- */
	const malformedUrls: string[] = [];
	for (const m of text.matchAll(URL_RE)) {
		const candidate = trimUrl(m[0]);
		let ok = true;
		try {
			const u = new URL(candidate);
			if (!u.hostname.includes('.') && u.hostname !== 'localhost') ok = false;
		} catch {
			ok = false;
		}
		if (!ok) malformedUrls.push(candidate);
	}
	for (const m of text.matchAll(URL_EMPTY_RE)) malformedUrls.push(m[0]);

	/* Beyanlar ----------------------------------------------------------- */
	const checks: AccessCheck[] = [];
	checks.push(check('data', data ? 'ok' : 'missing', data?.text));
	if (!data) {
		push({
			code: 'missing_data_availability',
			category: 'access',
			severity: 'warning',
			title: 'Veri erişimi beyanı yok',
			detail:
				'Metinde veri erişilebilirliği (data availability) ifadesi bulunamadı. Dergiler ve açık bilim ilkeleri bu beyanı bekler.',
			suggestion:
				'Verilerin nerede/ nasıl erişilebilir olduğunu belirtin (ör. "Veriler sorumlu yazardan talep üzerine temin edilebilir").',
			location: {},
			confidence: 'medium',
			decisive: true,
			meta: { check: 'data' }
		});
	}

	if (computational) {
		checks.push(check('code', code ? 'ok' : 'missing', code?.text));
		if (!code) {
			push({
				code: 'missing_code_availability',
				category: 'access',
				severity: 'info',
				title: 'Kod erişimi beyanı yok',
				detail:
					'Hesaplamalı bir çalışma gibi görünüyor; ancak kaynak kod veya kod erişimi bilgisi bulunamadı.',
				suggestion: 'Kod deposu (GitHub/GitLab/Zenodo) bağlantısı veya "kod talep üzerine" beyanı ekleyin.',
				location: {},
				confidence: 'medium',
				decisive: false,
				meta: { check: 'code' }
			});
		}
	} else {
		checks.push(check('code', 'not_applicable'));
	}

	checks.push(check('coi', coi ? 'ok' : 'missing', coi?.text));
	if (!coi) {
		push({
			code: 'missing_coi_statement',
			category: 'access',
			severity: 'warning',
			title: 'Çıkar çatışması beyanı yok',
			detail: 'Metinde çıkar çatışması (conflict of interest) beyanı bulunamadı.',
			suggestion: 'Yazarların çıkar çatışması olup olmadığını açıkça belirtin.',
			location: {},
			confidence: 'medium',
			decisive: true,
			meta: { check: 'coi' }
		});
	}

	checks.push(check('funding', funding ? 'ok' : 'missing', funding?.text));
	if (!funding) {
		push({
			code: 'missing_funding_statement',
			category: 'access',
			severity: 'info',
			title: 'Destek/fon beyanı yok',
			detail: 'Metinde fon veya destek beyanı bulunamadı.',
			suggestion: 'Destekleyen kurum ve proje numarası varsa ekleyin; yoksa "destek alınmadı" yazın.',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { check: 'funding' }
		});
	}

	if (human) {
		checks.push(check('ethics', ethics ? 'ok' : 'missing', ethics?.text));
		if (!ethics) {
			push({
				code: 'missing_ethics_statement',
				category: 'access',
				severity: 'warning',
				title: 'Etik beyanı yok',
				detail:
					'İnsan katılımcılarla yapılan bir çalışma gibi görünüyor; ancak etik kurul/onam beyanı bulunamadı.',
				suggestion: 'Etik kurul onayı (numara) ve bilgilendirilmiş onam bilgisini ekleyin.',
				location: {},
				confidence: 'medium',
				decisive: true,
				meta: { check: 'ethics' }
			});
		}
	} else {
		checks.push(check('ethics', 'not_applicable'));
	}

	checks.push(check('doi', invalidDois.length ? 'invalid' : 'ok', undefined, invalidDois.length));
	if (invalidDois.length) {
		push({
			code: 'invalid_doi_format',
			category: 'access',
			severity: 'warning',
			title: 'Geçersiz DOI biçimi',
			detail:
				`${invalidDois.length} DOI adayı "10.xxxx/..." biçimine uymuyor. ` +
				`Örnek: ${invalidDois.slice(0, 3).join(', ')}`,
			quote: invalidDois[0],
			suggestion: 'DOI numarasını 10.xxxx/… biçiminde ve eksiksiz yazın.',
			location: {},
			confidence: 'high',
			decisive: true,
			meta: { check: 'doi', count: invalidDois.length }
		});
	}

	checks.push(check('url', malformedUrls.length ? 'invalid' : 'ok', undefined, malformedUrls.length));
	if (malformedUrls.length) {
		push({
			code: 'malformed_url',
			category: 'access',
			severity: 'info',
			title: 'Bozuk URL biçimi',
			detail:
				`${malformedUrls.length} bağlantı geçerli bir URL gibi görünmüyor. ` +
				`Örnek: ${malformedUrls.slice(0, 3).join(', ')}`,
			quote: malformedUrls[0],
			suggestion: 'Bağlantıları https://alan-adi/yol biçiminde kontrol edin.',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { check: 'url', count: malformedUrls.length }
		});
	}

	// Metadata / blind-review leakage
	if (meta?.author && meta.author.trim().length > 1 && !GENERIC_AUTHOR_RE.test(meta.author.trim())) {
		push({
			code: 'access.metadata.author',
			category: 'access',
			severity: 'warning',
			title: 'Belge üstverisinde yazar adı',
			detail: `PDF üstverisi "${meta.author.trim()}" yazar adını içeriyor. Bu, çift kör hakemlik sürecini bozabilir.`,
			suggestion: 'Göndermeden önce PDF özelliklerinden yazar alanını kaldırın.',
			location: {},
			confidence: 'high',
			decisive: true,
			meta: { name: meta.author.trim() }
		});
	}

	if (
		meta?.creator &&
		meta.creator.trim().length > 1 &&
		!/^(Microsoft|Adobe|LaTeX|pdfTeX|Google|LibreOffice|Writer|Word|Overleaf)/i.test(meta.creator.trim())
	) {
		push({
			code: 'access.metadata.creator',
			category: 'access',
			severity: 'info',
			title: 'Üstveride standart dışı oluşturucu',
			detail: `PDF oluşturucu alanı "${meta.creator.trim()}" değerini içeriyor ve yazarı tanımlayabilir.`,
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { value: meta.creator.trim() }
		});
	}

	const emailMatches = [...text.matchAll(EMAIL_IN_TEXT_RE)];
	for (const m of emailMatches.slice(0, 5)) {
		const pos = m.index ?? 0;
		const context = text.slice(Math.max(0, pos - 200), pos);
		if (/(?:references|kaynakça|kaynaklar|bibliography)/i.test(context)) continue;
		push({
			code: 'access.metadata.email',
			category: 'access',
			severity: 'info',
			title: 'Makale gövdesinde e-posta adresi',
			detail: 'Makale metninde bir e-posta adresi görünüyor ve kör hakemliği tehlikeye atabilir.',
			quote: excerpt(text.slice(pos, pos + m[0].length), 220),
			suggestion: 'Kör hakemlik için gönderim yapıyorsanız e-posta adreslerini kaldırın.',
			location: index.locationAt(pos, pos + m[0].length),
			confidence: 'medium',
			decisive: false
		});
	}

	const orcid = [...text.matchAll(ORCID_RE)][0];
	if (orcid) {
		const pos = orcid.index ?? 0;
		push({
			code: 'access.metadata.orcid',
			category: 'access',
			severity: 'info',
			title: 'Makalede ORCID tanımlayıcısı',
			detail: 'Metinde bir ORCID iD görünüyor ve yazarı doğrudan tanımlıyor.',
			quote: excerpt(text.slice(pos, pos + orcid[0].length), 220),
			location: index.locationAt(pos, pos + orcid[0].length),
			confidence: 'high',
			decisive: false
		});
	}

	const missing = checks.filter((c) => c.status === 'missing').length;
	const invalid = checks.filter((c) => c.status === 'invalid').length;
	const ok = checks.filter((c) => c.status === 'ok').length;

	let score = 100;
	if (!data) score -= 14;
	if (!coi) score -= 12;
	if (human && !ethics) score -= 14;
	if (invalidDois.length) score -= 10;
	if (computational && !code) score -= 4;
	if (!funding) score -= 4;
	if (malformedUrls.length) score -= 4;
	score = Math.max(0, Math.min(100, score));

	const result: AccessResult = {
		checks,
		dois: validDois.size,
		invalidDois: invalidDois.length,
		urls: malformedUrls.length,
		computational,
		humanStudy: human,
		summary: { ok, missing, invalid, total: checks.length },
		score
	};

	return { result, issues };
}
